import { useState, useMemo, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check } from 'lucide-react';
import { Message, WidgetBlock } from '@/stores/chatStore';
import { useSettingsStore } from '@/stores/settingsStore';
import GeminiLoader from '@/components/Chat/GeminiLoader';
import ClaudeLoader from '@/components/Chat/ClaudeLoader';
import ToolUseCard from '@/components/Chat/ToolUseCard';
import InlineWidget from '@/components/Chat/widgets/InlineWidget';
import gsap from 'gsap';
import clsx from 'clsx';

interface HoloMessageProps {
  message: Message;
}

function CodeBlock({ language, children }: { language: string; children: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group my-2.5 hud-clip-md overflow-hidden border border-jarvis-blue/30">
      <div className="flex items-center justify-between px-4 py-1.5 bg-black/40 border-b border-jarvis-blue/30">
        <span className="text-[9px] font-mono text-jarvis-blue/60 uppercase tracking-wider">
          {language || 'code'}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-jarvis-blue transition-colors"
        >
          {copied ? (
            <>
              <Check size={10} /> Copied
            </>
          ) : (
            <>
              <Copy size={10} /> Copy
            </>
          )}
        </button>
      </div>
      <SyntaxHighlighter
        style={atomDark}
        language={language || 'text'}
        customStyle={{
          margin: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          padding: '0.75rem 1rem',
          fontSize: '0.8rem',
          borderRadius: 0,
        }}
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
}

function StreamingIndicator() {
  const provider = useSettingsStore((s) => s.modelPreference);

  if (provider === 'gemini') {
    return (
      <div className="flex items-center gap-2.5 py-1">
        <GeminiLoader size={24} />
        <span className="text-[10px] font-mono text-blue-400/50 tracking-wider">GEMINI</span>
      </div>
    );
  }

  if (provider === 'claude') {
    return (
      <div className="flex items-center gap-2.5 py-1">
        <ClaudeLoader size={24} />
        <span className="text-[10px] font-mono text-orange-400/50 tracking-wider">CLAUDE</span>
      </div>
    );
  }

  // Stark Protocol / default
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <div className="jarvis-scan-indicator w-16 h-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-jarvis-blue/5 border border-jarvis-blue/10"
          style={{ clipPath: 'polygon(0 3px, 3px 0, calc(100% - 3px) 0, 100% 3px, 100% calc(100% - 3px), calc(100% - 3px) 100%, 3px 100%, 0 calc(100% - 3px))' }}
        />
        <div className="jarvis-scan-bar absolute top-0 bottom-0 w-5 opacity-80"
          style={{ background: 'linear-gradient(90deg, transparent, #00d4ff, transparent)' }}
        />
      </div>
      <span className="text-[10px] font-mono text-jarvis-blue/40 tracking-wider">PROCESSING</span>
    </div>
  );
}

export default function HoloMessage({ message }: HoloMessageProps) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const msgRef = useRef<HTMLDivElement>(null);
  const [thinkingExpanded, setThinkingExpanded] = useState(false);

  // Strip leaked tool tags, thinking blocks, and metadata
  const { displayContent, parsedWidgets } = useMemo(() => {
    let text = message.content;
    // Remove {{TOGGLE_VOICE:off}} style tags
    text = text.replace(/\{\{\w+:\w+\}\}/g, '');
    // Remove <think>...</think> tags
    text = text.replace(/<think>[\s\S]*?<\/think>\s*/gi, '');
    // Strip leaked prompt injection / tool context that shouldn't be displayed
    text = text.replace(/\n*-{3}\nVERIFIED DATA \(use this to answer[^)]*\):[\s\S]*?\[SYSTEM: Tool results[^\]]*\]\s*/g, '');
    text = text.replace(/\[SYSTEM:[^\]]*\]\s*/g, '');
    text = text.replace(/\[(?:landmark_search|weather|list_calendar_events|search_contacts|navigate|web_search|now_playing|media_control|health_summary|date_time|news|sports|set_reminder|recall_memory|delete_calendar_event|read_email|send_email|send_imessage|get_my_location|calculator|wolfram_alpha)\]:\s*[^\n]*/g, '');
    // Fix "Sir" capitalization: lowercase mid-sentence, keep at start
    text = text.replace(/(?<=[a-z,;—–\-])\s+Sir\b/g, (m) => m.toLowerCase());
    // Strip thinking blocks: if text starts with "Thinking Process:" etc,
    // extract only the actual response from the end
    if (/^(?:Thinking Process|Analyze the Request|Drafting|Determine|Refining)/im.test(text)) {
      const paragraphs = text.split(/\n{2,}/);
      for (let i = paragraphs.length - 1; i >= 0; i--) {
        const p = paragraphs[i].trim();
        if (p && !/^(?:Option \d|Thinking|Analyze|Determine|Drafting|Refining|Selecting|Persona|Voice|Address|Style|Input|Task|Tone|Response|Concise|Status|Standard|Chain)/i.test(p)
            && !p.startsWith('- ') && !p.startsWith('* ') && !/^\d+\./.test(p)) {
          text = paragraphs.slice(i).join('\n\n');
          break;
        }
      }
      text = text.replace(/^Option \d[^.]*\.\s*(?:Let's go with that\.\s*)?/i, '');
    }

    // Extract [WIDGET:{...}] markers
    const widgets: WidgetBlock[] = [];
    text = text.replace(/\[WIDGET:(.*?)\]\n?/g, (_match, json) => {
      try {
        const w = JSON.parse(json);
        if (w && w.type && w.data) widgets.push(w);
      } catch { /* ignore malformed */ }
      return '';
    });

    return { displayContent: text.trim(), parsedWidgets: widgets };
  }, [message.content]);

  const timeDisplay = useMemo(() => {
    if (!message.timestamp) return '';
    const date = new Date(message.timestamp);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, [message.timestamp]);

  // GSAP entry animation
  useEffect(() => {
    if (msgRef.current && !message.isStreaming) {
      gsap.fromTo(
        msgRef.current,
        {
          opacity: 0,
          y: 12,
          scale: 0.98,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.4,
          ease: 'power3.out',
        },
      );
    }
  }, []);

  // System/error messages
  if (isSystem) {
    return (
      <div className="flex justify-center my-2 animate-fade-in">
        <div className="glass-subtle hud-clip-sm px-4 py-2 max-w-lg border-hud-red/10">
          <p className="text-[11px] text-center text-gray-400 font-mono">{displayContent}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={msgRef}
      className={clsx('flex mb-4', {
        'justify-end': isUser,
        'justify-start': !isUser,
      })}
    >
      <div className={clsx('max-w-[80%] relative group')}>
        {/* Label */}
        {!isUser && (
          <div className="flex items-center gap-2 mb-1.5">
            <img src="/arc-reactor-icon.png" alt="" className="w-4 h-4 flex-shrink-0 opacity-70" />
            <span className="hud-label text-[8px]">J.A.R.V.I.S.</span>
            <span className="text-[7px] font-mono text-jarvis-blue/20">{timeDisplay}</span>
          </div>
        )}
        {/* Tool cards above bubble */}
        {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mb-1.5">
            {message.toolCalls.map((tc) => (
              <ToolUseCard key={tc.id} toolCall={tc} />
            ))}
          </div>
        )}

        {isUser && (
          <div className="flex items-center justify-end gap-1.5 mb-1.5">
            <span className="text-[7px] font-mono text-jarvis-gold/20">{timeDisplay}</span>
            <span className="text-[9px] font-mono text-jarvis-gold/50 tracking-wider uppercase">
              You
            </span>
          </div>
        )}

        {/* Thinking expandable */}
        {!isUser && message.thinkingContent && (
          <div className="mb-1.5">
            <button
              onClick={() => setThinkingExpanded(!thinkingExpanded)}
              className="flex items-center gap-1.5 px-2 py-1 text-[9px] font-mono text-gray-500 hover:text-gray-400 transition-colors"
            >
              <span className={`transition-transform ${thinkingExpanded ? 'rotate-90' : ''}`}>&#9656;</span>
              <span>{message.isThinkingDone ? 'Thought' : 'Thinking'}</span>
              {!message.isThinkingDone && <span className="animate-pulse">...</span>}
            </button>
            {thinkingExpanded && (
              <div className="ml-4 mt-1 max-h-32 overflow-y-auto text-[10px] font-mono text-gray-600 leading-relaxed border-l border-gray-700/30 pl-2">
                {message.thinkingContent}
              </div>
            )}
          </div>
        )}

        {/* Streaming indicator — show when waiting for content (even during thinking) */}
        {!isUser && message.isStreaming && !message.content && (
          <div className="px-1 py-1">
            <StreamingIndicator />
          </div>
        )}

        {/* Message bubble — only when there's actual content */}
        {(isUser || displayContent) && (
        <div
          className={clsx('hud-clip-md text-sm leading-relaxed', {
            'glass-gold text-white/90 px-5 py-3.5': isUser,
            'glass-cyan text-gray-200 px-5 py-3.5': !isUser,
          })}
        >
          {(
            <div className="prose prose-invert prose-sm max-w-none font-sans">
              <ReactMarkdown
                components={{
                  code({ className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || '');
                    const isInline = !match;
                    const codeString = String(children).replace(/\n$/, '');

                    if (isInline) {
                      return (
                        <code
                          className="px-1.5 py-0.5 bg-black/30 text-jarvis-cyan text-xs font-mono rounded-md border border-white/[0.05]"
                          {...props}
                        >
                          {children}
                        </code>
                      );
                    }
                    return <CodeBlock language={match[1]}>{codeString}</CodeBlock>;
                  },
                  p({ children }) {
                    return <p className="mb-2 last:mb-0">{children}</p>;
                  },
                  ul({ children }) {
                    return <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>;
                  },
                  ol({ children }) {
                    return <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>;
                  },
                  a({ href, children }) {
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-jarvis-blue hover:text-jarvis-cyan underline underline-offset-2"
                      >
                        {children}
                      </a>
                    );
                  },
                  blockquote({ children }) {
                    return (
                      <blockquote className="border-l-2 border-jarvis-blue/30 pl-3 my-2 text-gray-400 italic">
                        {children}
                      </blockquote>
                    );
                  },
                }}
              >
                {displayContent}
              </ReactMarkdown>
            </div>
          )}

          {message.isStreaming && message.content && (
            <span className="inline-block w-0.5 h-4 bg-jarvis-cyan ml-0.5 animate-pulse" />
          )}
        </div>
        )}

        {/* Timestamp — shows on hover (hidden since we moved it inline) */}
        {/* Inline Widgets */}
        {!isUser && parsedWidgets.length > 0 && (
          <div className="mt-2">
            {parsedWidgets.map((w, i) => (
              <InlineWidget key={`${w.type}-${i}`} widget={w} />
            ))}
          </div>
        )}

        <div
          className={clsx(
            'text-[9px] text-gray-600 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity font-mono',
            { 'text-right': isUser, 'text-left': !isUser },
          )}
        >
          {timeDisplay}
        </div>
      </div>
    </div>
  );
}
